import { createContext, useContext, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { get, set } from 'local-storage'
import { useRouter } from 'next/router'

import { ASSET_MANAGEMENT_MODULES } from '../config/constants/navigation'
import { useRoles } from '../utils/ensureRole'
import {
  findBestNavigationMatch,
  MappedNavItem,
  NavGroup,
  NavigationConfig,
  NavItem,
  SettingsConfig,
} from '../utils/navigationConfig'

import { useLocale } from './localeContext'
import { useProject } from './projectContext'
import { useBuilderSubscription } from './subscriptionContext'

import { useEnabledModules } from '@/hooks/useEnabledModules'
import { GET_PROJECT_SCHEDULES_FOR_NAV } from '@/modules/scheduling/graphQL'
import { GET_LOGGED_IN_USER_REGISTERS } from '@/modules/user/graphQL'

import { useUser } from 'src/context/userContext'

import {
  ArchivedFilter,
  BuilderTier,
  BuildPassModule,
  LoggedInUserRegistersUiQuery,
  LoggedInUserRegistersUiQueryVariables,
  ProjectSchedulesForNavUiQuery,
  ProjectSchedulesForNavUiQueryVariables,
} from 'types/api.graphql'
import { QuerySupport } from 'types/navigation'

const LS_KEY = 'buildpass-nav-sections'

type NavigationStorage = {
  sections: Record<string, boolean>
}

const DEFAULT_STORAGE: NavigationStorage = {
  sections: {},
}

type Value = {
  navigationConfig: NavGroup[]
  settingsConfig: NavItem[]
  currentNavItem?: NavItem
  isModuleEnabled: (module: BuildPassModule) => boolean
  isSectionExpanded: (sectionId: string) => boolean
  setSectionExpanded: (sectionId: string, expanded: boolean) => void
}

const NavigationContext = createContext<Value>({
  navigationConfig: undefined,
  settingsConfig: undefined,
  currentNavItem: undefined,
  isModuleEnabled: () => false,
  isSectionExpanded: () => true,
  setSectionExpanded: () => {},
})

type Props = {
  children?: React.ReactNode
}

export const NavigationProvider: React.FC<Props> = ({ children }) => {
  const { t } = useLocale()

  const lsStorage = get<NavigationStorage>(LS_KEY) ?? DEFAULT_STORAGE
  const [storage, setStorage] = useState(lsStorage)

  const { roles } = useRoles()
  const { enabledModules, isModuleEnabled } = useEnabledModules()
  const { hasSubscription } = useBuilderSubscription()
  const { asPath, pathname } = useRouter()
  const { project, isProjectSelected } = useProject()
  const {
    dashboards,
    builder,
    adminUser,
    checklistCategories,
    permissions,
    settings,
  } = useUser()

  const { data: registersData } = useQuery<
    LoggedInUserRegistersUiQuery,
    LoggedInUserRegistersUiQueryVariables
  >(GET_LOGGED_IN_USER_REGISTERS, {
    skip: !isModuleEnabled(BuildPassModule.Registers),
    variables: {
      filter: {
        archivedFilter: ArchivedFilter.ActiveOnly,
        projectId: isProjectSelected ? project.id : undefined,
      },
    },
  })

  const { data: projectSchedulesData } = useQuery<
    ProjectSchedulesForNavUiQuery,
    ProjectSchedulesForNavUiQueryVariables
  >(GET_PROJECT_SCHEDULES_FOR_NAV, {
    skip: !isProjectSelected || !isModuleEnabled(BuildPassModule.Scheduling),
    variables: { projectId: project?.id },
  })

  const memoedFavourites = useMemo(() => {
    return settings?.favourites
  }, [settings?.favourites])

  const canAccessChecklistCategories = useMemo(() => {
    return hasSubscription(BuilderTier.Standard)
  }, [hasSubscription])

  const selectedProjectId = isProjectSelected ? project?.id : undefined
  const disableCompanyInductions =
    builder?.builderMetadata?.disableCompanyInductions

  const navigationConfig = useMemo(() => {
    const registers = registersData?.registers ?? []

    if (!roles.length || registers === undefined) return

    return new NavigationConfig({
      userRoles: roles,
      userEnabledModules: enabledModules,
      userIsLimitedPermissions: adminUser.limitedPermissions,
      userPermittedModules: permissions,
      translate: t,
    })
      .removeDisabledCompanyInduction(disableCompanyInductions)
      .addChecklistCategories(canAccessChecklistCategories, checklistCategories)
      .addDashboards(dashboards, selectedProjectId)
      .addRegisters(registers)
      .addFavourites(memoedFavourites)
      .configureSchedulingModule(projectSchedulesData)
      .get()
  }, [
    registersData?.registers,
    roles,
    enabledModules,
    adminUser.limitedPermissions,
    permissions,
    disableCompanyInductions,
    canAccessChecklistCategories,
    checklistCategories,
    dashboards,
    selectedProjectId,
    memoedFavourites,
    projectSchedulesData,
    t,
  ])

  const settingsConfig = useMemo(() => {
    return new SettingsConfig({
      userRoles: roles,
      userEnabledModules: enabledModules,
      userIsLimitedPermissions: adminUser.limitedPermissions,
      userPermittedModules: permissions,
      translate: t,
    }).get()
  }, [roles, enabledModules, adminUser, permissions, t])

  const currentNavItem = useMemo<NavItem>(() => {
    if (!navigationConfig || !settingsConfig) {
      return
    }

    const mapItem = (item: NavItem) =>
      item.subItems ? item.subItems.flatMap(mapItem) : [item]

    const allNavItems: MappedNavItem[] = [
      ...(navigationConfig?.flatMap((group) => group.items) || []),
      ...settingsConfig,
      ASSET_MANAGEMENT_MODULES.items.find((i) => i.id === 'registers'),
    ]
      ?.flatMap(mapItem)
      ?.filter((item) => item)

    const navItem = findBestNavigationMatch(asPath, allNavItems)

    if (!navItem) {
      return navItem as NavItem
    }

    let querySupport: QuerySupport
    if (typeof navItem.querySupport === 'object') {
      querySupport = navItem.querySupport[pathname]
        ? navItem.querySupport[pathname]
        : navItem.querySupport.default
    } else {
      querySupport = navItem.querySupport
    }

    return { ...navItem, querySupport }
  }, [navigationConfig, settingsConfig, asPath, pathname])

  const setNewStorage = (newStorage: NavigationStorage) => {
    set(LS_KEY, newStorage)
    setStorage(newStorage)
  }

  const isSectionExpanded = (sectionId: string) => {
    return storage.sections[sectionId] ?? true // Default to expanded
  }

  const setSectionExpanded = (sectionId: string, expanded: boolean) => {
    const newStorage = {
      ...storage,
      sections: {
        ...storage.sections,
        [sectionId]: expanded,
      },
    }
    setNewStorage(newStorage)
  }

  return (
    <NavigationContext.Provider
      value={{
        navigationConfig,
        settingsConfig,
        currentNavItem,
        isModuleEnabled,
        isSectionExpanded,
        setSectionExpanded,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = () => useContext(NavigationContext)
